// React, store and 3rd party
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

// Components Icons
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import { DataGrid } from '@mui/x-data-grid';

// Store
import { breadcrumbsActions } from '@store/breadcrumbs';
import { usePermissions } from '@store/auth';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import {
  getPaymentMethods,
  paymentOptionsMethodsActions,
  selectMethodList,
} from '../../store/payment-options-methods';

import {
  METHOD_LIST_ROUTE,
  METHOD_NEW_ROUTE,
  viewPaymentMethods,
} from '../../payment-options.breadcrumbs';

import TableToolbar from '../../../../components/TableToolbar';

function MethodsToolbar({ navigate, permissions }) {
  return (
    <TableToolbar>
      <Button
        disabled={!permissions?.canEditPaymentOptions}
        variant="contained"
        size="large"
        onClick={() => navigate(METHOD_NEW_ROUTE)}
        startIcon={<AddIcon />}>
        Add new method
      </Button>
    </TableToolbar>
  );
}

export default function PaymentMethods() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const permissions = usePermissions();

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [pageSize, setPageSize] = useState(20);

  const methods = useSelector(selectMethodList);

  const getModifiedDetails = ({ row }) =>
    moment(row.modifiedAt).format('YYYY/MM/DD - HH:mm');

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewPaymentMethods));
    dispatch(paymentOptionsMethodsActions.resetSelectedMethod());
  }, [dispatch]);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(getPaymentMethods()).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  return (
    <ApplicationPageTemplate
      title="Payment Methods"
      description="List of the payment methods in the system. You can use the page to add and update payment methods."
      pageReady={!isFetchingData}>
      <DataGrid
        density="compact"
        style={{ background: 'white' }}
        rows={methods}
        pageSize={pageSize}
        onPageSizeChange={(params) => setPageSize(params.pageSize)}
        rowsPerPageOptions={[10, 20, 50, 100]}
        disableColumnFilter
        disableColumnMenu
        disableDensitySelector
        components={{
          Toolbar: () => MethodsToolbar({ navigate, permissions }),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        onRowClick={({ row }) =>
          navigate(`${METHOD_LIST_ROUTE}/${row.methodId}`)
        }
        getRowId={(row) => row.methodId}
        columns={[
          {
            field: 'methodId',
            headerName: 'ID',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'methodName',
            headerName: 'Method Name',
            flex: 1,
            sortable: true,
            headerAlign: 'left',
          },
          {
            field: 'methodCode',
            headerName: 'Method Code',
            flex: 1,
            sortable: true,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'modifiedBy',
            headerName: 'Modified By',
            flex: 1,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'modifiedAt',
            headerName: 'Last Modified Date',
            flex: 1,
            sortable: true,
            headerAlign: 'right',
            align: 'right',
            valueGetter: getModifiedDetails,
          },
        ]}
      />
    </ApplicationPageTemplate>
  );
}
